(() => {
  if (customElements.get('leslis-withdrawal-accessibility')) return;

  class LeslisWithdrawalAccessibility extends HTMLElement {
    connectedCallback() {
      this.ownedAttributes = new Map();
      this.inertElements = new Map();
      this.guards = [];
      this.recordOpener = this.recordOpener.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onFocus = this.onFocus.bind(this);
      this.schedule = this.schedule.bind(this);
      document.addEventListener('click', this.recordOpener, true);
      document.addEventListener('keydown', this.onKeydown, true);
      document.addEventListener('focusin', this.onFocus, true);
      this.treeObserver = new MutationObserver(this.schedule);
      this.treeObserver.observe(document.body, { childList: true, subtree: true });
      this.sync();
    }

    disconnectedCallback() {
      this.treeObserver?.disconnect();
      this.overlayObserver?.disconnect();
      document.removeEventListener('click', this.recordOpener, true);
      document.removeEventListener('keydown', this.onKeydown, true);
      document.removeEventListener('focusin', this.onFocus, true);
      this.release();
      this.overlay = null;
    }

    schedule() {
      if (this.pending) return;
      this.pending = true;
      queueMicrotask(() => {
        this.pending = false;
        if (this.isConnected) this.sync();
      });
    }

    recordOpener(event) {
      if (this.modal || !(event.target instanceof Element)) return;
      const link = event.target.closest('a[href$="#vertrag-widerrufen"]');
      if (link && link.origin === window.location.origin) {
        this.opener = link;
        return;
      }
      const button = event.target.closest('.hb-widerruf-button-wrapper button');
      if (button && this.contains(button) && button.getClientRects().length) this.opener = button;
    }

    sync() {
      const overlay = document.getElementById('hbRevocationModalOverlay');
      if (overlay !== this.overlay) {
        this.release();
        this.overlayObserver?.disconnect();
        this.overlay = overlay;
        if (overlay) {
          this.overlayObserver = new MutationObserver(this.schedule);
          this.overlayObserver.observe(overlay, {
            attributes: true,
            attributeFilter: ['class', 'style', 'hidden'],
          });
        }
      }

      const modal = overlay?.querySelector('#hbRevocationModal.hb-modal');
      const close = modal?.querySelector('button.hb-modal-close');
      const open = overlay?.classList.contains('active') && !overlay.hidden &&
        getComputedStyle(overlay).display !== 'none' &&
        getComputedStyle(overlay).visibility !== 'hidden';

      if (!open || !modal || !close) {
        this.release();
        return;
      }

      const opening = this.modal !== modal;
      if (opening) {
        this.release();
        this.modal = modal;
      }
      this.closeButton = close;
      this.setAttributeFor(modal, 'role', 'dialog', true);
      if (!modal.getAttribute('aria-labelledby')) {
        this.setAttributeFor(modal, 'aria-label', this.dataset.dialogLabel, true);
      }
      this.setAttributeFor(modal, 'data-leslis-withdrawal-accessible', 'true');
      this.setAttributeFor(close, 'aria-label', this.dataset.closeLabel, true);

      const frame = modal.querySelector('iframe#hbRevocationModalIframe');
      if (frame) this.setAttributeFor(frame, 'title', this.dataset.frameLabel, true);
      this.frame = frame;
      this.ensureGuards();
      this.isolateBackground();
      this.setAttributeFor(modal, 'aria-modal', 'true');
      if (opening) close.focus({ preventScroll: true });
    }

    setAttributeFor(element, name, value, onlyWhenEmpty = false) {
      if (!value || (onlyWhenEmpty && element.getAttribute(name)?.trim())) return;
      if (element.getAttribute(name) === value) return;
      let attributes = this.ownedAttributes.get(element);
      if (!attributes) {
        attributes = new Map();
        this.ownedAttributes.set(element, attributes);
      }
      if (!attributes.has(name)) attributes.set(name, { before: element.getAttribute(name), applied: value });
      element.setAttribute(name, value);
    }

    ensureGuards() {
      if (this.guards.length) {
        if (this.modal.firstElementChild !== this.guards[0]) this.modal.prepend(this.guards[0]);
        if (this.modal.lastElementChild !== this.guards[1]) this.modal.append(this.guards[1]);
        return;
      }
      const before = document.createElement('span');
      const after = document.createElement('span');
      for (const guard of [before, after]) {
        guard.className = 'leslis-withdrawal-focus-guard';
        guard.tabIndex = 0;
      }
      before.addEventListener('focus', () => (this.frame || this.closeButton)?.focus({ preventScroll: true }));
      after.addEventListener('focus', () => this.closeButton?.focus({ preventScroll: true }));
      this.modal.prepend(before);
      this.modal.append(after);
      this.guards = [before, after];
    }

    isolateBackground() {
      let branch = this.overlay;
      while (branch?.parentElement) {
        const parent = branch.parentElement;
        for (const sibling of parent.children) {
          if (sibling === branch || ['SCRIPT', 'STYLE', 'LINK'].includes(sibling.tagName)) continue;
          if (!this.inertElements.has(sibling)) this.inertElements.set(sibling, sibling.getAttribute('inert'));
          if (!sibling.hasAttribute('inert')) sibling.setAttribute('inert', '');
        }
        if (parent === document.body) break;
        branch = parent;
      }
    }

    onFocus(event) {
      if (this.modal && !this.modal.contains(event.target)) this.closeButton?.focus({ preventScroll: true });
    }

    onKeydown(event) {
      if (!this.modal || event.key !== 'Escape' || event.isComposing) return;
      event.preventDefault();
      event.stopPropagation();
      this.closeButton.click();
    }

    release() {
      const wasOpen = Boolean(this.modal);
      this.modal = null;
      this.closeButton = null;
      this.frame = null;
      for (const guard of this.guards || []) guard.remove();
      this.guards = [];
      for (const [element, before] of this.inertElements || []) {
        if (before === null) element.removeAttribute('inert');
        else element.setAttribute('inert', before);
      }
      this.inertElements?.clear();
      for (const [element, attributes] of this.ownedAttributes || []) {
        for (const [name, { before, applied }] of attributes) {
          if (element.getAttribute(name) !== applied) continue;
          if (before === null) element.removeAttribute(name);
          else element.setAttribute(name, before);
        }
      }
      this.ownedAttributes?.clear();
      if (wasOpen && this.opener?.isConnected && !this.opener.disabled) {
        this.opener.focus({ preventScroll: true });
      }
    }
  }

  customElements.define('leslis-withdrawal-accessibility', LeslisWithdrawalAccessibility);
})();
